/**
 * change-password-dialog.tsx — Đổi mật khẩu đăng nhập (POST /auth/change-password).
 *
 * Backend yêu cầu mật khẩu mới ≥ 8 ký tự và thu hồi toàn bộ refresh token sau
 * khi đổi — mọi phiên (kể cả phiên này) sẽ phải đăng nhập lại khi hết hạn.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useChangePassword } from '@/hooks/use-settings'
import { apiError } from '@/lib/api-client'

const MIN_PASSWORD = 8

export function ChangePasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation(['accountPages', 'common'])
  const changePassword = useChangePassword()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')

  const reset = () => {
    setCurrent('')
    setNext('')
    setConfirm('')
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (next.length < MIN_PASSWORD) return toast.error(t('accountPages:changePassword.tooShort', { count: MIN_PASSWORD }))
    if (next !== confirm) return toast.error(t('accountPages:changePassword.mismatch'))
    if (next === current) return toast.error(t('accountPages:changePassword.sameAsCurrent'))
    try {
      await changePassword.mutateAsync({ currentPassword: current, newPassword: next })
      reset()
      onOpenChange(false)
      toast.success(t('accountPages:changePassword.success'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset()
        onOpenChange(o)
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('accountPages:changePassword.title')}</DialogTitle>
          <DialogDescription>
            {t('accountPages:changePassword.description')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="cur-pw">{t('accountPages:changePassword.currentLabel')}</Label>
            <Input id="cur-pw" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1" autoComplete="current-password" autoFocus />
          </div>
          <div>
            <Label htmlFor="new-pw">{t('accountPages:changePassword.newLabel')}</Label>
            <Input id="new-pw" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1" autoComplete="new-password" />
            <p className="mt-1 text-xs text-muted-foreground">{t('accountPages:changePassword.minHint', { count: MIN_PASSWORD })}</p>
          </div>
          <div>
            <Label htmlFor="cf-pw">{t('accountPages:changePassword.confirmLabel')}</Label>
            <Input id="cf-pw" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" autoComplete="new-password" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" disabled={changePassword.isPending || !current || !next || !confirm}>
              {changePassword.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('accountPages:changePassword.title')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
